import { action, computed, makeAutoObservable, observable } from "mobx"
import { CheckIsShowBundleData, PBAPI, PkgListData } from "react-pp-bundle"

import APIPlan from "@/api/plan"
import { IPackageList } from "@/api/plan/type"

import { PlanEnum, statusListProps } from "./type"
export default class PlanStore {
  @observable perferPlanId?: number
  @action.bound
  setPerferPlanId(val?: number) {
    this.perferPlanId = val
  }
  @observable perferKind?: number
  @action.bound
  setPerferKind(val?: number) {
    this.perferKind = val
  }
  @observable bundlePeriod: number = 1 // 1：月度 2：年度
  @action.bound
  setBundlePeriod(val: number) {
    this.bundlePeriod = val
  }
  @observable appType: "returns" | "bundle" = "returns"
  @action.bound
  setAppType(val: "returns" | "bundle") {
    this.appType = val
  }
  @observable type: number = 1 // 1：月度 2：年度
  // 配置数据
  @observable config: IPackageList | null = null
  // 当前plan步骤
  @observable curPlanStep: Record<PlanEnum, number> = {
    [PlanEnum.Free]: 0,
    [PlanEnum.Essential]: 0,
    [PlanEnum.Professional]: 0,
    [PlanEnum.Enterprise]: 0,
  }
  // 当前Billing展示内容
  @observable curBilling: number = 0
  // 当前Billing展示内容
  @observable curTab: number = 0
  // 是否展示免费退货服务文本
  @observable showReturnServiceShow: number = 0
  // 设置状态值
  @observable statusList: statusListProps = {
    isFree: false, // 记录当前选择降套餐
    Free: false, // 当前订阅套餐的loading
    Essential: false, // essentail按钮的loading
    Professional: false, // profession按钮的loading
    Enterprise: false, // enterprise按钮的loading
    showReminderModel: false, // 设置组合套餐
  }

  constructor() {
    // 使用这个才会在 MobX 6 上才会更新视图
    makeAutoObservable(this)
  }

  @action.bound
  updateCurrentStep() {
    // 订阅bundle套餐，都默认显示第一条
    if (this.config?.user_pkg?.source === 3) {
      this.curPlanStep = {
        [PlanEnum.Free]: 0,
        [PlanEnum.Essential]: 0,
        [PlanEnum.Professional]: 0,
        [PlanEnum.Enterprise]: 0,
      }
      return
    }
    // 初始化 设置当前月、年套餐（凡是有订阅过的，都定位到年套餐）
    const { user_pkg } = this.config || {}
    this.type = user_pkg?.package_period || 1
    const {
      package_id,
      package_kind = 1,
      package_period = 1,
      package_name,
      recommend_plan_kind,
      recommend_package_id,
    } = user_pkg || {}
    const findPrice =
      this.completedPackageList[package_kind - 1]?.priceInfo[package_period]
    const index = findPrice?.findIndex((item) => item.id === package_id)
    const newCurPlanStep = {
      [PlanEnum.Free]: 0,
      [PlanEnum.Essential]: 0,
      [PlanEnum.Professional]: 0,
      [PlanEnum.Enterprise]: 0,
    }
    if (package_name) {
      newCurPlanStep[package_name as PlanEnum] = index === -1 ? 0 : index
    }
    if (recommend_package_id && recommend_plan_kind) {
      const recommendPlanItem = this.completedPackageList.find(
        (item) => item.kind === recommend_plan_kind,
      )
      const monthlyPricing = recommendPlanItem?.priceInfo[1]
      const recommendIndex = monthlyPricing?.findIndex(
        (item) => item.id === recommend_package_id,
      )
      if (
        recommendPlanItem &&
        recommendIndex !== undefined &&
        recommendIndex !== -1
      ) {
        newCurPlanStep[recommendPlanItem.package_name as PlanEnum] =
          recommendIndex
      }
    }
    this.curPlanStep = newCurPlanStep
    // 初始化，设置当前激活的选项
  }

  @action.bound
  setConfig(info: IPackageList) {
    this.config = info
    this.updateCurrentStep()
    this.showReturnServiceShow = info?.user_pkg?.plan_return_service_show
  }

  @observable stepOfUpgrade: number = 0

  @action.bound
  setStepOfUpgrade(val: number) {
    this.stepOfUpgrade = val
  }

  @observable loading: boolean = false

  @action.bound
  setLoading(val: boolean) {
    this.loading = val
  }

  @observable bundleLoading: boolean = false

  @action.bound
  setBundleLoading(val: boolean) {
    this.bundleLoading = val
  }

  @observable bundleData: PkgListData | null = null
  @action.bound
  setBundleData(val: PkgListData) {
    this.bundleData = val
  }

  @observable showBannerData: CheckIsShowBundleData | null = null
  @action.bound
  setCheckIsShowBannerData(val: CheckIsShowBundleData) {
    this.showBannerData = val
  }

  @action.bound
  async getBundleData() {
    this.setBundleLoading(true)
    try {
      const res2 = await PBAPI.checkIsShowBundle()
      if (res2.code === 1000 && res2.data) {
        this.setCheckIsShowBannerData(res2.data)
      }
      if (res2.data.show_bundle) {
        const res = await PBAPI.getBundleList()
        if (res.code === 1000 && res.data) {
          this.setBundleData(res.data)
        }
      }
    } catch (error) {
      console.error(error)
    }
    this.setBundleLoading(false)
  }

  @action.bound
  async updatePlanData() {
    await Promise.all([this.getPlanConfig(), this.getBundleData()])
  }

  @action.bound
  async getPlanConfig() {
    this.setLoading(true)
    // 请求套餐列表信息
    const res = await APIPlan.getPackageList()
    const { code, data } = res || {}
    if (code === 1000) {
      this.setConfig(data)
    }
    this.setLoading(false)
  }

  @action.bound
  setType(val: number) {
    this.type = val
    // 切换按钮，进度条设置对应的默认值
    const { user_pkg } = this.config || {}
    // 当前type类型，没有当前套餐，则设置默认值
    if (user_pkg?.package_period !== val) {
      this.setIniteCurPlanStep()
      return
    }
    // 有当前套餐情况
    const {
      package_id,
      package_kind = 1,
      package_period = 1,
      package_name,
    } = user_pkg || {}
    const findPrice =
      this.completedPackageList[package_kind - 1]?.priceInfo[package_period]
    const len = findPrice?.findIndex((item) => item.id === package_id)
    // 初始化，设置当前激活的选项 todo
    const newCurPlanStep = {
      [PlanEnum.Free]: 0,
      [PlanEnum.Essential]: 0,
      [PlanEnum.Professional]: 0,
      [PlanEnum.Enterprise]: 0,
      [package_name]: len,
    }
    this.curPlanStep = newCurPlanStep
  }

  // 获取当前拖动选择套餐info
  @computed get curPackageInfo() {
    const { user_pkg, pkg_list } = this.config || {}
    const newPackageInfo = pkg_list
      ?.find((item) => item.kind === user_pkg?.package_kind)
      ?.pricing?.find((item) => item.id === user_pkg?.package_id)
    return newPackageInfo
  }

  @computed get completedPackageList() {
    const { pkg_list } = this.config || {}
    if (!pkg_list) return []

    return pkg_list
      .map((item) => {
        return {
          ...item,
          priceInfo: {
            1: item.pricing
              .filter((price) => price.period === 1)
              ?.map((price) => ({
                ...price,
                actual_price: price.actual_price?.toString().includes(".")
                  ? price.actual_price.toFixed(2)
                  : price.actual_price,
              })),
            2: item.pricing
              .filter((price) => price.period === 2)
              .map((price) => ({
                ...price,
                actual_price: (price.actual_price / 12).toString().includes(".")
                  ? (price.actual_price / 12).toFixed(2)
                  : price.actual_price / 12,
                origin_price: (price.actual_price / 12).toString().includes(".")
                  ? (price.actual_price / 12).toFixed(2)
                  : price.actual_price / 12,
              })),
          },
        }
      })
      .sort((a, b) => a.kind - b.kind) // 按照 kind 从小到大排序
  }

  // 获取当前套餐info
  @computed get completedCurPlan() {
    const { pkg_list = [], user_pkg } = this.config || {}
    const { package_kind = 1, package_id } = user_pkg || {}
    const newPackageList: any = pkg_list
      ?.find((item) => item.kind === package_kind)
      ?.pricing?.find((item) => item.id === package_id)
    return newPackageList
  }

  @action.bound
  getPlanItemAndItemPeriodInfo(package_kind: number, type: number) {
    const planItem = this.completedPackageList.find(
      (item) => item.kind === package_kind,
    )
    if (!planItem) return null
    const itemPeriodInfo = planItem?.priceInfo[type]
    return { planItem, itemPeriodInfo }
  }

  @action.bound
  getInfoByStep(package_kind: number, type: number, step: number) {
    const info = this.getPlanItemAndItemPeriodInfo(package_kind, type)
    if (!info) return null
    const { planItem, itemPeriodInfo } = info
    const current = itemPeriodInfo[step]
    return { planItem, itemPeriodInfo, current }
  }

  @action.bound
  getInfoByPricingId(package_kind: number, type: number, pricingId: number) {
    const info = this.getPlanItemAndItemPeriodInfo(package_kind, type)
    if (!info) return null
    const { planItem, itemPeriodInfo } = info
    const index = itemPeriodInfo.findIndex((item) => item.id === pricingId)
    return {
      planItem,
      itemPeriodInfo,
      step: index === -1 ? 0 : index,
      current: itemPeriodInfo[index],
    }
  }

  // 设置状态值
  @action.bound
  setStatusListByPath(path: string, val: boolean) {
    this.statusList[path] = val
  }

  @action.bound
  setCurPlanStep(plan: PlanEnum, val: number) {
    this.curPlanStep[plan] = val
  }

  @action.bound
  setCurBilling(val: number) {
    this.curBilling = val
  }

  @action.bound
  setCurTab(val: number) {
    this.curTab = val
  }

  @action.bound
  setShowReturnServiceShow(val: number) {
    this.showReturnServiceShow = val
  }

  @action.bound
  setIniteCurPlanStep() {
    const newCurPlanStep = {
      [PlanEnum.Free]: 0,
      [PlanEnum.Essential]: 0,
      [PlanEnum.Professional]: 0,
      [PlanEnum.Enterprise]: 0,
    }
    this.curPlanStep = newCurPlanStep
  }
}
